import { UncontrolledTooltip } from 'reactstrap';
import { TYPE_BEAT_ENUM, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { defaultControlModalValue } from '@/entities/report';
import {
  IconButton,
  BlackDeleteIcon as blackDeleteIcon,
  BlackExpandHorizontalIcon as blackExpandHorizontalIcon,
  BlackNoiseIcon as blackNoiseIcon,
  BlackUndoIcon as blackUndoIcon,
  ChangeEventIcon as changeEventIcon,
  UnreviewedIcon as reviewUnmarkIcon,
  ReviewedIcon16 as reviewMarkIcon,
} from '@/shared/ui';
import BulkActionByEvent from '@/features/bulk-event-action';
import { EventThumbnailConfig } from '@/entities/ecg';
import FilterEvents from '@/features/filter-events';
import AfibPaginationContainer from './afibPaginationContainer';
import useAfibButtonTypes from './useAfibButtonTypes';

/**
 * Toolbar above the AFib event strips, exposing per-event edit actions and list controls. Renders change type, delete, mark as artifact, change duration and mark reviewed buttons with keyboard shortcut tooltips, a reset button once changes exist, and the thumbnail config, bulk action, filter and pagination controls.
 *
 * @returns The rendered AFib toolbar with its action buttons and list controls.
 */
const AfibButtonTypes = () => {
  const {
    keyRecoil,
    isLoadingStrip,
    activeButton,
    isGroupEventChanges,
    setPageIndex,
    isDisableChangingButton,
    isDisableChangeDuration,
    setControlModal,
    getControlModal,
    handleChangeStripType,
    onClickMarkReview,
    onClickReset,
    isEventReviewed,
    isShowResetButton,
  } = useAfibButtonTypes();

  return (
    <div
      id="button-types"
      className="beat-type-strip-content-top-container --min-width-strip-event"
    >
      <div className="toolbar">
        <IconButton
          id="btnChangeEventTypeBeatHr"
          isOutline
          iconComponent={<img src={changeEventIcon} alt="Change Event icon" />}
          disabled={isDisableChangingButton}
          onClick={() => {
            setControlModal({ ...defaultControlModalValue, isShowChangeTypeBulk: true });
          }}
        />
        <UncontrolledTooltip
          className="custom-tooltip"
          target="btnChangeEventTypeBeatHr"
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          <div className="beat-tooltip">Change event type</div>
        </UncontrolledTooltip>

        <IconButton
          id="btnDeleteBeatHr"
          isOutline
          iconComponent={<img src={blackDeleteIcon} alt="Delete icon" />}
          disabled={isDisableChangingButton}
          onClick={() => handleChangeStripType(TYPE_BEAT_ENUM.DELETED)}
        />
        <UncontrolledTooltip
          className="custom-tooltip"
          target="btnDeleteBeatHr"
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          <div className="beat-tooltip">
            Delete event
            <span className="shortkey">D</span>
          </div>
        </UncontrolledTooltip>

        <IconButton
          id="btnChangeArtifactBeatHr"
          isOutline
          iconComponent={<img src={blackNoiseIcon} alt="Noise icon" />}
          disabled={isDisableChangingButton}
          onClick={() => handleChangeStripType(TYPE_RHYTHM_EVENT_ENUM.ARTIFACT)}
        />
        <UncontrolledTooltip
          className="custom-tooltip"
          target="btnChangeArtifactBeatHr"
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          <div className="beat-tooltip">
            Mark as artifact
            <span className="shortkey">A</span>
          </div>
        </UncontrolledTooltip>

        <IconButton
          id="btnChangeEventDurationBeatHr"
          isOutline
          iconComponent={<img src={blackExpandHorizontalIcon} alt="Expand horizontal icon" />}
          disabled={isDisableChangeDuration}
          onClick={() => {
            if (!getControlModal().isShowChangeDurationButtonType) {
              setControlModal({
                ...defaultControlModalValue,
                isShowChangeDurationButtonType: true,
              });
            }
          }}
        />
        <UncontrolledTooltip
          className="custom-tooltip"
          target="btnChangeEventDurationBeatHr"
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          <div className="beat-tooltip">
            Change event duration
            <span className="shortkey">C</span>
          </div>
        </UncontrolledTooltip>

        <IconButton
          id="btnMarkReviewBeatHr"
          isOutline
          iconComponent={
            <img src={isEventReviewed ? reviewMarkIcon : reviewUnmarkIcon} alt="Review mark icon" />
          }
          disabled={isDisableChangingButton}
          onClick={() => onClickMarkReview(!isEventReviewed)}
        />
        <UncontrolledTooltip
          className="custom-tooltip"
          target="btnMarkReviewBeatHr"
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          <div className="beat-tooltip">
            {isEventReviewed ? 'Unmark as reviewed' : 'Mark as reviewed'}
            <span className="shortkey">R</span>
          </div>
        </UncontrolledTooltip>
        {!!isShowResetButton && (
          <>
            <div className="btn-type-vertical-line" />
            <IconButton
              id="btnResetBeatHr"
              isOutline
              iconComponent={<img src={blackUndoIcon} alt="Noise icon" />}
              disabled={isDisableChangingButton}
              onClick={onClickReset}
            />
            <UncontrolledTooltip
              className="custom-tooltip"
              target="btnResetBeatHr"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
            >
              <div className="beat-tooltip">Reset changes</div>
            </UncontrolledTooltip>
          </>
        )}
      </div>
      <EventThumbnailConfig isAfib isDisabled={isLoadingStrip} />
      <div className="toolbar-container">
        <BulkActionByEvent
          isDisabled={isLoadingStrip}
          keyRecoil={keyRecoil}
          activeButton={activeButton}
          hasChanged={isGroupEventChanges}
          className="mr3"
        />
        <FilterEvents
          activeButton={activeButton}
          keyRecoil={keyRecoil}
          isDisabled={isLoadingStrip}
          setPageIndex={setPageIndex}
        />
        <AfibPaginationContainer isDisabled={isLoadingStrip} activeButton={activeButton} />
      </div>
    </div>
  );
};

export default AfibButtonTypes;
